import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import api from "../../api";
import { LEVEL_REQUIREMENTS } from "../../constants/items";
import "./ManageItem.css";
import "../../components/Profile/PlayerProfileTabs.css";

// Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

const ManageItems = () => {
  document.title = "Pokemon Radiance  | Manage Items";

  // Tab state: "all", "equip", "chests"
  const [activeTab, setActiveTab] = useState("all");
  // Inventory from /v1/inventory endpoint
  const [inventory, setInventory] = useState([]);
  // Full item details from /v1/store endpoint
  const [storeItems, setStoreItems] = useState([]);
  // Loading state for common data
  const [loading, setLoading] = useState(true);
  // Search query for filtering items in All Items
  const [searchQuery, setSearchQuery] = useState("");
  // Mining level for equipment requirement checks
  const [miningLevel, setMiningLevel] = useState(0);

  // Equipment data from /v1/equipment endpoint
  const [equipData, setEquipData] = useState(null);
  const [equipLoading, setEquipLoading] = useState(true);

  const [equipmentLoadouts, setEquipmentLoadouts] = useState([]);
  const [equipmentLoadoutsLoading, setEquipmentLoadoutsLoading] = useState(false);
  const [equipmentLoadoutNames, setEquipmentLoadoutNames] = useState({});
  const [equipmentLoadoutsExpanded, setEquipmentLoadoutsExpanded] = useState(false);
  const [loadoutToast, setLoadoutToast] = useState(null);

  // Popup state variables
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupColor, setPopupColor] = useState("");

  const [candiesState, setCandiesState] = useState(null);
  const [candiesLoading, setCandiesLoading] = useState(false);
  const [usingCandyId, setUsingCandyId] = useState(null);
  const [lastCandyCategory, setLastCandyCategory] = useState(null);

  // New state for chest opening animation (only for color boxes)
  const [chestOpening, setChestOpening] = useState(false);
  // Using array destructuring with empty variable to avoid lint warnings
  const [, setChestCandidates] = useState([]);
  const [, setFinalChestPokemon] = useState(null);
  const [chestDisplayImage, setChestDisplayImage] = useState(null);
  const [chestRewardItems, setChestRewardItems] = useState(null);
  const [chestModalTitle, setChestModalTitle] = useState("Opening Chest");

  // Fetch inventory and store items on mount
  useEffect(() => {
    const fetchCommonData = async () => {
      try {
        const [inventoryRes, storeRes, miningRes] = await Promise.all([
          api.get("/v1/inventory", { withCredentials: true }),
          api.get("/v1/store", { withCredentials: true }),
          api.get("/v1/mining/info", { withCredentials: true }),
        ]);
        // Filter out any items with amount 0.
        setInventory(
          inventoryRes.data.filter(
            (item) => item.amount > 0 || item.stack_amount > 0,
          ),
        );
        setStoreItems(storeRes.data);
        // Set mining level for equipment requirement checks
        setMiningLevel(miningRes.data.mining_level || 0);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching common data:", error);
        setLoading(false);
      }
    };
    fetchCommonData();
  }, []);

  // When active tab is Equip Items, fetch equipment data
  useEffect(() => {
    if (activeTab === "equip") {
      const fetchEquipData = async () => {
        try {
          setEquipLoading(true);
          setEquipmentLoadoutsLoading(true);

          const [res, loadoutsRes] = await Promise.all([
            api.get("/v1/equipment", { withCredentials: true }),
            api.get("/v1/equipment/loadouts", { withCredentials: true }),
          ]);

          setEquipData(res.data);
          const loadouts = loadoutsRes?.data?.loadouts || [];
          setEquipmentLoadouts(loadouts);
          setEquipmentLoadoutNames((prev) => {
            const next = { ...prev };
            loadouts.forEach((l) => {
              const slot = Number(l?.slot) || 0;
              if (!slot) return;
              if (typeof next[slot] === "string") return;
              next[slot] = l?.name || "";
            });
            return next;
          });
          setEquipLoading(false);
          setEquipmentLoadoutsLoading(false);
        } catch (error) {
          console.error("Error fetching equipment data:", error);
          setEquipLoading(false);
          setEquipmentLoadoutsLoading(false);
        }
      };
      fetchEquipData();
    }
  }, [activeTab]);

  const refreshEquipmentAndInventory = async () => {
    const [equipRes, invRes] = await Promise.all([
      api.get("/v1/equipment", { withCredentials: true }),
      api.get("/v1/inventory", { withCredentials: true }),
    ]);
    setEquipData(equipRes.data);
    setInventory(invRes.data.filter((item) => item.amount > 0 || item.stack_amount > 0));
  };

  const refreshEquipmentLoadouts = async () => {
    const loadoutsRes = await api.get("/v1/equipment/loadouts", { withCredentials: true });
    const loadouts = loadoutsRes?.data?.loadouts || [];
    setEquipmentLoadouts(loadouts);
    setEquipmentLoadoutNames((prev) => {
      const next = { ...prev };
      loadouts.forEach((l) => {
        const slot = Number(l?.slot) || 0;
        if (!slot) return;
        next[slot] = l?.name || "";
      });
      return next;
    });
  };

  const handleSaveEquipmentLoadout = async (slot) => {
    try {
      setEquipmentLoadoutsLoading(true);
      const name = (equipmentLoadoutNames?.[slot] || "").trim() || null;
      await api.post(
        "/v1/equipment/loadouts/save",
        { slot, name },
        { withCredentials: true }
      );
      await refreshEquipmentLoadouts();
      setEquipmentLoadoutsLoading(false);
      setLoadoutToast({
        title: "Loadout saved.",
        items: buildLoadoutPreviewItems(slot),
        tone: "success",
      });
      setTimeout(() => setLoadoutToast(null), 2500);
    } catch (error) {
      console.error("Error saving loadout:", error);
      setEquipmentLoadoutsLoading(false);
      setLoadoutToast({
        title: error.response?.data?.detail || "Failed to save loadout.",
        items: buildLoadoutPreviewItems(slot),
        tone: "error",
      });
      setTimeout(() => setLoadoutToast(null), 5000);
    }
  };

  const buildLoadoutPreviewItems = (slot) => {
    const meta = (equipmentLoadouts || []).find((l) => Number(l?.slot) === Number(slot)) || null;
    const eq = meta?.equipment || null;
    if (!eq) return [];

    const items = [];
    const pushIf = (itemId, amount) => {
      const id = Number(itemId) || 0;
      if (!id) return;
      items.push({ item_id: id, amount: Number(amount) || 1 });
    };

    pushIf(eq?.head?.item, eq?.head?.stack);
    pushIf(eq?.neck?.item, eq?.neck?.stack);
    pushIf(eq?.body?.item, eq?.body?.stack);
    pushIf(eq?.handhelds?.item, eq?.handhelds?.stack);
    pushIf(eq?.feet?.item, eq?.feet?.stack);
    pushIf(eq?.ring1, 1);
    pushIf(eq?.ring2, 1);
    pushIf(eq?.ring3, 1);

    return items;
  };

  const renderLoadoutIcons = (slot, maxIcons = 8) => {
    const items = buildLoadoutPreviewItems(slot);
    const sliced = items.slice(0, maxIcons);
    if (!sliced.length) return null;

    return (
      <div className="equipment-loadout-icons">
        {sliced.map((it, idx) => {
          const item = storeItemsMap[it.item_id];
          const img = item?.image ? `/${item.image}` : null;
          const name = item?.name || `Item ${it.item_id}`;
          const amount = Number(it.amount || 0);

          return (
            <div
              key={`loadout-icon-${slot}-${it.item_id}-${idx}`}
              className="equipment-loadout-icon"
              title={name}
            >
              {img ? (
                <img
                  src={img}
                  alt={name}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = "none";
                  }}
                />
              ) : (
                <div className="equipment-loadout-icon-fallback" />
              )}
              {amount > 1 && <div className="equipment-loadout-icon-count">×{amount}</div>}
            </div>
          );
        })}
      </div>
    );
  };

  const handleApplyEquipmentLoadout = async (slot) => {
    try {
      setEquipmentLoadoutsLoading(true);
      const previewItems = buildLoadoutPreviewItems(slot);
      setLoadoutToast({
        title: "Applying loadout...",
        items: previewItems,
        tone: "loading",
      });

      await api.post(
        "/v1/equipment/loadouts/apply",
        { slot },
        { withCredentials: true }
      );
      await refreshEquipmentAndInventory();
      await refreshEquipmentLoadouts();

      setEquipmentLoadoutsLoading(false);
      setLoadoutToast({
        title: "Loadout applied.",
        items: previewItems,
        tone: "success",
      });
      setTimeout(() => setLoadoutToast(null), 2500);
    } catch (error) {
      console.error("Error applying loadout:", error);
      setEquipmentLoadoutsLoading(false);
      setLoadoutToast({
        title: error.response?.data?.detail || "Failed to apply loadout.",
        items: buildLoadoutPreviewItems(slot),
        tone: "error",
      });
      setTimeout(() => setLoadoutToast(null), 5000);
    }
  };

  useEffect(() => {
    if (activeTab !== "candies") return;
    const fetchCandiesState = async () => {
      setCandiesLoading(true);
      try {
        const res = await api.get("/v1/candies/state", { withCredentials: true });
        setCandiesState(res.data);
      } catch (error) {
        console.error("Error fetching candies state:", error);
      } finally {
        setCandiesLoading(false);
      }
    };
    fetchCandiesState();
  }, [activeTab]);

  const ownedCandyRows = (candiesState?.candies || []).filter((c) => Number(c?.amount || 0) > 0);

  const handleUseCandy = async (candyId) => {
    if (usingCandyId) return;
    setUsingCandyId(candyId);
    try {
      const candyRow = (candiesState?.candies || []).find((c) => Number(c.id) === Number(candyId));
      const candyImg = candyRow?.image ? `/${candyRow.image}` : "/items/pokeball.png";
      const candyName = candyRow?.name || `Candy ${candyId}`;

      setChestRewardItems(null);
      setChestDisplayImage(candyImg);
      setPopupMessage(`Using ${candyName}...`);
      setChestModalTitle("Using Candy");
      setChestOpening(true);

      const res = await api.post(
        "/v1/candies/use",
        { candy_id: candyId },
        { withCredentials: true }
      );
      const gained = res.data?.gained_xp;
      const cat = res.data?.category;
      const nextState = res.data?.state;
      const mintAwarded = res.data?.mint_awarded;

      if (cat) {
        setLastCandyCategory(cat);
        setTimeout(() => setLastCandyCategory(null), 1200);
      }

      if (nextState) {
        setCandiesState(nextState);
      }

      refreshInventory();

      setPopupMessage(
          typeof gained === "number"
            ? `Candy used! +${gained.toLocaleString()} ${categoryLabel(cat)} XP`
          : "Candy used!"
      );
    } catch (error) {
      console.error("Error using candy:", error);
      setPopupMessage(error.response?.data?.detail || "Failed to use candy.");
      setChestModalTitle("Using Candy");
      setChestOpening(true);
    } finally {
      setUsingCandyId(null);
    }
  };

  const categoryLabel = (key) => {
    if (key === "trainer") return "Trainer";
    if (key === "catching") return "Catching";
    if (key === "fishing") return "Fishing";
    if (key === "mining") return "Mining";
    if (key === "farming") return "Farming";
    return key;
  };

  const CANDY_UI = [
    {
      key: "trainer",
      candyId: 215,
      label: "Trainer",
      icon: "/items/battleseek.png",
      accent: "#ef4444",
      bg: "linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.05) 100%)",
      border: "1px solid rgba(239, 68, 68, 0.3)",
      shadow: "0 4px 12px rgba(239, 68, 68, 0.2)",
    },
    {
      key: "catching",
      candyId: 216,
      label: "Catching",
      icon: "/items/pokeball.png",
      accent: "#22c55e",
      bg: "linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(22, 163, 74, 0.05) 100%)",
      border: "1px solid rgba(34, 197, 94, 0.3)",
      shadow: "0 4px 12px rgba(34, 197, 94, 0.2)",
    },
    {
      key: "mining",
      candyId: 218,
      label: "Mining",
      icon: "/items/NormalPickaxe.png",
      accent: "#d97706",
      bg: "linear-gradient(135deg, rgba(161, 98, 7, 0.15) 0%, rgba(120, 53, 15, 0.05) 100%)",
      border: "1px solid rgba(161, 98, 7, 0.3)",
      shadow: "0 4px 12px rgba(161, 98, 7, 0.2)",
    },
    {
      key: "fishing",
      candyId: 217,
      label: "Fishing",
      icon: "/items/superrod.png",
      accent: "#3b82f6",
      bg: "linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.05) 100%)",
      border: "1px solid rgba(59, 130, 246, 0.3)",
      shadow: "0 4px 12px rgba(59, 130, 246, 0.2)",
    },
    {
      key: "farming",
      candyId: 219,
      label: "Farming",
      icon: "/items/razzberry.png",
      accent: "#fb923c",
      bg: "linear-gradient(135deg, rgba(251, 146, 60, 0.15) 0%, rgba(249, 115, 22, 0.05) 100%)",
      border: "1px solid rgba(251, 146, 60, 0.3)",
      shadow: "0 4px 12px rgba(251, 146, 60, 0.2)",
    },
  ];

  // Define safe and chest (color box) item IDs.
  const safeItemIds = [58, 222258, 333358, 64];
  const chestBoxIds = [180, 181, 182, 183, 184, 185, 186];

  const equipableInStock = (equipData?.equipable || []).filter(
    (item) => item.amount > 0
  );

  // Filter the inventory to include only safe and chest items with amount > 0.
  const chestInventory = inventory.filter(
    (invItem) =>
      (safeItemIds.includes(invItem.item) ||
        chestBoxIds.includes(invItem.item)) &&
      invItem.amount > 0,
  );

  // Create a mapping from item id to its full details (name, image, etc.)
  const storeItemsMap = storeItems.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});
  // Also create mapping from item name to item details (for equipped items lookup)
  const storeItemsByName = storeItems.reduce((acc, item) => {
    acc[item.name] = item;
    return acc;
  }, {});

  // Create mapping for inventory by item id (only items with amount > 0).
  const inventoryMap = inventory.reduce((acc, inv) => {
    if (inv.amount > 0 || inv.stack_amount > 0) {
      const key = inv.item ?? inv.id;
      acc[key] = inv;
    }
    return acc;
  }, {});
  // Filter inventory for All Items tab based on search query and amount > 0.
  const filteredInventory = inventory.filter((invItem) => {
    const item = storeItemsMap[invItem.item];
    if (!item) return false;
    return (
      invItem.amount > 0 &&
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  // Handler to unequip an item by item id.
  const handleUnequip = async (itemId) => {
    try {
      const charmIds = new Set([193, 194, 121]);
      const equipableCharm = charmIds.has(itemId)
        ? (equipData?.equipable || []).find((item) => item.id === itemId)
        : null;

      if (equipableCharm && equipableCharm.stack_amount) {
        for (let i = 0; i < equipableCharm.stack_amount; i += 1) {
          await api.post(
            "/v1/unequip",
            { item: itemId },
            { withCredentials: true },
          );
        }
      } else {
        await api.post(
          "/v1/unequip",
          { item: itemId },
          { withCredentials: true },
        );
      }
      // Refresh equipment data.
      const res = await api.get("/v1/equipment", { withCredentials: true });
      setEquipData(res.data);
      // Refresh inventory data.
      const invRes = await api.get("/v1/inventory", { withCredentials: true });
      setInventory(
        invRes.data.filter((item) => item.amount > 0 || item.stack_amount > 0),
      );
    } catch (error) {
      console.error("Error unequipping item:", error);
      let message = "Error unequipping item.";
      if (
        error.response.data &&
        error.response.data &&
        error.response.data.detail
      ) {
        message = error.response.detail;
      }
      setPopupMessage(message);
      setPopupColor("red");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 5000);
    }
  };

  // Handler to equip an item.
  const handleEquip = async (itemId) => {
    // Check if the item has a mining level requirement
    if (LEVEL_REQUIREMENTS[itemId] && miningLevel < LEVEL_REQUIREMENTS[itemId]) {
      const requiredLevel = LEVEL_REQUIREMENTS[itemId];
      setPopupMessage(`You need mining level ${requiredLevel} to equip this item.`);
      setPopupColor("red");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 5000);
      return;
    }

    try {
      const charmIds = new Set([193, 194, 121]);
      const equipableCharm = charmIds.has(itemId)
        ? (equipData?.equipable || []).find((item) => item.id === itemId)
        : null;

      if (equipableCharm) {
        const maxStack = equipableCharm.max_stack_amount > 0
          ? equipableCharm.max_stack_amount
          : equipableCharm.amount;
        const remaining = Math.max(
          0,
          maxStack - (equipableCharm.stack_amount || 0),
        );

        for (let i = 0; i < remaining; i += 1) {
          await api.post("/v1/equip", { item: itemId }, { withCredentials: true });
        }
      } else {
        await api.post("/v1/equip", { item: itemId }, { withCredentials: true });
      }

      // Refresh equipment data.
      const res = await api.get("/v1/equipment", { withCredentials: true });
      setEquipData(res.data);
      // Refresh inventory data.
      const invRes = await api.get("/v1/inventory", { withCredentials: true });
      setInventory(
        invRes.data.filter((item) => item.amount > 0 || item.stack_amount > 0),
      );
    } catch (error) {
      console.error("Error equipping item:", error);
      let message = "Error equipping item.";
      setChestDisplayImage(null);
      setPopupMessage(message);
      setPopupColor("red");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 5000);
    }
  };

  // Handler for safe items with modern modal display.
  const handleUseItem = async (itemId) => {
    try {
      // Get the item details for display
      const safeItemDetails = storeItemsMap[itemId];
      setChestRewardItems(null);
      setChestModalTitle(`Opening ${safeItemDetails?.name || 'Item'}`);
      
      // Show opening animation
      setChestOpening(true);
      setChestDisplayImage(`/${safeItemDetails?.image || 'items/safe.png'}`);
      setPopupMessage(`Opening ${safeItemDetails?.name || 'Safe'}...`);
      setPopupColor("#1d222e");
      
      // Call API to open the safe
      const res = await api.post(
        "/v1/use_item",
        { item_id: itemId },
        { withCredentials: true },
      );
      
      // Update the display with results
      if (res.data?.gems_awarded && Array.isArray(res.data.gems_awarded)) {
        setChestRewardItems(res.data.gems_awarded);
        setPopupMessage(`You opened a ${safeItemDetails?.name || 'Bag of Gems'}!`);
      } else {
        setPopupMessage(res.data.message);
      }
      setChestDisplayImage(`/${safeItemDetails?.image || 'items/safe.png'}`);
      
      // Refresh inventory after using the item but keep modal open
      // The user will close the modal manually with the close button
      refreshInventory();
    } catch (error) {
      console.error("Error using item:", error);
      let message = "Error opening safe.";
      if (error.response?.data?.detail) {
        message = error.response.data.detail;
      }
      
      // Show error in modal
      setChestOpening(true);
      setPopupMessage(message);
      // Error color is now handled by the fixed white text color
      // Keep modal open for user to close manually
    }
  };
  
  // Helper function to refresh inventory
  const refreshInventory = async () => {
    try {
      const invRes = await api.get("/v1/inventory", { withCredentials: true });
      setInventory(
        invRes.data.filter((item) => item.amount > 0 || item.stack_amount > 0),
      );
    } catch (error) {
      console.error("Error refreshing inventory:", error);
    }
  };

  // Handler for chest boxes (color boxes) with flipping animation.
  const handleUseChest = async (itemId) => {
    try {
      setChestModalTitle("Opening Chest");
      // Reset chest-related state for a clean start.
      setFinalChestPokemon(null);
      setChestCandidates([]);
      setChestDisplayImage(null);
      setPopupMessage(null);
      setPopupColor(null);
      setChestRewardItems(null);

      // First, call the use_item endpoint (POST) for the chest box.
      const useRes = await api.post(
        "/v1/use_item",
        { item_id: itemId },
        { withCredentials: true },
      );
      const chestData = useRes.data;
      // Expected response format:
      // { "type": "luminous", "pokemon_name": "LuminousPichu", "gender": "F", "level": 5 }
      setFinalChestPokemon(chestData);

      // Then, call the GET endpoint for that box type from account "l33t".
      const typeRes = await api.get(`/v1/pokemon/type/economy/${chestData.type}`, {
        withCredentials: true,
      });
      const candidates = typeRes.data; // an array of candidate Pokémon.
      setChestCandidates(candidates);

      // Begin the image flipping animation (only for chest boxes).
      setChestOpening(true);
      const intervalId = setInterval(() => {
        if (candidates.length > 0) {
          const randomCandidate =
            candidates[Math.floor(Math.random() * candidates.length)];
          // Construct candidate image URL.
          const randomImageUrl = `/sprites/${randomCandidate.name}.png`;
          setPopupMessage("Randomly selecting a Pokemon.....");
          setPopupColor("gray");
          setChestDisplayImage(randomImageUrl);
        }
      }, 300);

      const categoryColor = (pokemonName) => {
        if (pokemonName.toLowerCase().includes("ruby")) return "#FF0000";
         if (pokemonName.toLowerCase().includes("sapphire")) return "#0F52BA";
        if (pokemonName.toLowerCase().includes("golden")) return "#d9c200";
        if (pokemonName.toLowerCase().includes("diamond")) return "#85c7ff";
        if (pokemonName.toLowerCase().includes("emerald")) return "#15ff00";
        if (pokemonName.toLowerCase().includes("shadow")) return "#2a0c32";
        return "#00ff00"; // Default green for regular Pokémon
      };

      // After 6 seconds, stop the flipping and show the final image.
      setTimeout(() => {
        clearInterval(intervalId);
        const finalImageUrl = `/sprites/${chestData.pokemon_name}.png`;
        setChestDisplayImage(finalImageUrl);
        
        // Hide gender if it's 'U' (unknown)
        const genderDisplay = chestData.gender === 'U' ? '' : `${chestData.gender} `;
        
        setPopupMessage(
          `You opened a ${chestData.type} box and received ${chestData.pokemon_name} ${genderDisplay}Lvl ${chestData.level.toLocaleString()}!`,
        );
        setPopupColor(categoryColor(chestData.pokemon_name));
        // Keep the chest opening modal open instead of switching to popup
        // setChestOpening(false);
        // setShowPopup(true);
        // setTimeout(() => setShowPopup(false), 5000);
        // Refresh inventory.
        api
          .get("/v1/inventory", { withCredentials: true })
          .then((invRes) =>
            setInventory(
              invRes.data.filter(
                (item) => item.amount > 0 || item.stack_amount > 0,
              ),
            ),
          );
      }, 6000);
    } catch (error) {
      console.error("Error using chest item:", error);
      let message = "Error using chest item.";
      if (
        error.response.data &&
        error.response.data &&
        error.response.data.detail
      ) {
        message = error.response.detail;
      }
      setChestDisplayImage(null);
      setPopupMessage(message);
      setPopupColor("red");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 5000);
    }
  };

  const formatNumber = (num) => {
    return Number(num).toLocaleString();
  };

  const getChestTone = (name = "") => {
    const value = String(name).toLowerCase();
    if (value.includes("ruby")) return "ruby";
    if (value.includes("sapphire")) return "sapphire";
    if (value.includes("emerald")) return "emerald";
    if (value.includes("diamond")) return "diamond";
    if (value.includes("golden")) return "golden";
    if (value.includes("shadow")) return "shadow";
    if (value.includes("gems") || value.includes("safe")) return "safe";
    return "basic";
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Pokémon Radiance" breadcrumbItem="Manage Items" />
          <Row className="mb-4">
            <Col>
              <Nav tabs>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "all" })}
                    onClick={() => setActiveTab("all")}
                    style={{ cursor: "pointer" }}
                  >
                    All Items
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "equip" })}
                    onClick={() => setActiveTab("equip")}
                    style={{ cursor: "pointer" }}
                  >
                    Equip Items
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "chests" })}
                    onClick={() => setActiveTab("chests")}
                    style={{ cursor: "pointer" }}
                  >
                    Chests
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "candies" })}
                    onClick={() => setActiveTab("candies")}
                    style={{ cursor: "pointer" }}
                  >
                    Candies
                  </NavLink>
                </NavItem>
              </Nav>
              <TabContent activeTab={activeTab} className="p-3">
                {/* All Items Tab */}
                <TabPane tabId="all">
                  {loading ? (
                    <div className="loading-container">
                      <div className="loading-spinner"></div>
                      <span>Loading items...</span>
                    </div>
                  ) : (
                    <div className="equipment-container all-items-shell profile-inventory">
                      <div className="equipment-header all-items-header">
                        <h1 className="equipment-title">Inventory</h1>
                        <p className="equipment-subtitle">View your item collection</p>
                      </div>

                      <div className="inventory-section all-items-panel profile-inventory__panel">
                        <div className="inventory-header all-items-toolbar profile-inventory__toolbar">
                          <div className="search-container all-items-search profile-inventory__search">
                            <input
                              type="text"
                              className="search-input"
                              placeholder="Search items..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <div className="search-icon">
                              🔍
                            </div>
                          </div>
                        </div>

                        <div className="inventory-grid all-items-grid profile-inventory__grid">
                          {filteredInventory.map((invItem) => {
                            const item = storeItemsMap[invItem.item];
                            if (!item) return null;
                            return (
                              <div key={invItem.item} className="inventory-item all-items-card profile-inventory__card">
                                <img
                                  src={`/${item.image}`}
                                  alt={item.name}
                                  className="inventory-item-image"
                                />
                                <div className="inventory-item-name all-items-name">{item.name}</div>
                                <div className="inventory-item-quantity all-items-qty">
                                  {formatNumber(invItem.amount)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        
                        {filteredInventory.length === 0 && (
                          <div className="profile-inventory__empty">
                            <p>No items found</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </TabPane>
                {/* Professional Equipment Tab */}
                <TabPane tabId="equip">
                  {equipLoading || !equipData ? (
                    <div className="loading-container">
                      <div className="loading-spinner"></div>
                      <span>Loading equipment...</span>
                    </div>
                  ) : (
                    <div className="equipment-container equipment-manager">
                      <div className="equipment-header">
                        <h1 className="equipment-title">Equipment Manager</h1>
                        <p className="equipment-subtitle">Equip items to enhance your character's abilities</p>
                      </div>

                      <div className={`equipment-loadouts-panel ${equipmentLoadoutsExpanded ? "expanded" : "collapsed"}`.trim()}>
                        <div className="equipment-loadouts-panel-header">
                          <div className="equipment-loadouts-title">Loadouts</div>
                          <button
                            className="equipment-loadouts-toggle"
                            onClick={() => setEquipmentLoadoutsExpanded((v) => !v)}
                            type="button"
                          >
                            {equipmentLoadoutsExpanded ? "Hide" : "Show"}
                          </button>
                        </div>

                        {!equipmentLoadoutsExpanded ? (
                          <div className="equipment-loadouts-compact">
                            {[1, 2, 3, 4, 5].map((slot) => {
                              const meta = (equipmentLoadouts || []).find((l) => Number(l?.slot) === slot) || null;
                              const saved = !!meta?.saved;
                              return (
                                <button
                                  key={`loadout-compact-${slot}`}
                                  className={
                                    saved
                                      ? "equipment-loadout-compact-btn saved"
                                      : "equipment-loadout-compact-btn"
                                  }
                                  onClick={() => handleApplyEquipmentLoadout(slot)}
                                  disabled={!saved || equipmentLoadoutsLoading}
                                  type="button"
                                  title={saved ? (meta?.name || `Slot ${slot}`) : "Empty"}
                                >
                                  <div className="equipment-loadout-compact-inner">
                                    <div className="equipment-loadout-compact-slot">{slot}</div>
                                    {saved && renderLoadoutIcons(slot, 5)}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="equipment-loadouts-grid">
                            {[1, 2, 3, 4, 5].map((slot) => {
                              const meta = (equipmentLoadouts || []).find((l) => Number(l?.slot) === slot) || null;
                              const saved = !!meta?.saved;
                              const nameVal = equipmentLoadoutNames?.[slot] ?? (meta?.name || "");

                              return (
                                <div
                                  key={`loadout-${slot}`}
                                  className="equipment-loadout-card"
                                >
                                  <div className="equipment-loadout-header">
                                    <div className="equipment-loadout-slot">Slot {slot}</div>
                                    <div className="equipment-loadout-status">{saved ? "Saved" : "Empty"}</div>
                                  </div>

                                  {saved && renderLoadoutIcons(slot, 8)}

                                  <input
                                    type="text"
                                    value={nameVal}
                                    onChange={(e) =>
                                      setEquipmentLoadoutNames((prev) => ({
                                        ...prev,
                                        [slot]: e.target.value,
                                      }))
                                    }
                                    placeholder="Name (optional)"
                                    className="equipment-loadout-input"
                                  />

                                  <div className="equipment-loadout-actions">
                                    <button
                                      onClick={() => handleSaveEquipmentLoadout(slot)}
                                      disabled={equipmentLoadoutsLoading}
                                      className="equipment-loadout-btn equipment-loadout-btn-save"
                                      type="button"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => handleApplyEquipmentLoadout(slot)}
                                      disabled={!saved || equipmentLoadoutsLoading}
                                      className="equipment-loadout-btn equipment-loadout-btn-load"
                                      type="button"
                                    >
                                      Load
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <div className="equipment-manager-grid">
                        <div className="equipment-manager-left">
                          {/* Character Equipment Display */}
                          <div className="character-equipment-section">
                            {/* Avatar moved to the left side */}
                            <div className="character-display">
                              <div className="character-avatar">
                                <img
                                  src={`/avatars/${localStorage.getItem('avatar') || '1'}.png`}
                                  alt="Player Avatar"
                                  className="player-avatar-image"
                                  onError={(e) => {
                                    e.target.src = '/avatars/default.png';
                                  }}
                                />
                              </div>
                            </div>

                            <div className="equipment-slots">
                              {["head", "neck", "body", "handhelds", "feet"].map((slot) => {
                                const equippedName = equipData.equipped[slot];
                                const itemDetail = equippedName ? storeItemsByName[equippedName] : null;
                                const invRecord = itemDetail ? inventoryMap[itemDetail.id] : null;
                                
                                return (
                                  <div 
                                    key={slot} 
                                    className={`equipment-slot slot-${slot} ${equippedName ? 'occupied' : ''}`}
                                    title={equippedName || `Empty ${slot} slot`}
                                  >
                                    {equippedName && itemDetail ? (
                                      <>
                                        <img
                                          src={`/${itemDetail.image}`}
                                          alt={equippedName}
                                          className="equipment-item-image"
                                        />
                                        <div className="equipment-actions">
                                          <button
                                            className="action-button unequip-button"
                                            onClick={() => handleUnequip(itemDetail.id)}
                                            title="Unequip item"
                                          >
                                            ✕
                                          </button>
                                        </div>
                                        <div className="equipment-tooltip">
                                          {equippedName}
                                          {invRecord && ` (${invRecord.stack_amount})`}
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                 
                                        <div className="equipment-tooltip">
                                          Empty {slot} slot
                                        </div>
                                      </>
                                    )}
                                    <div className="equipment-slot-label">{slot}</div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Ring slots section - positioned underneath the feet */}
                          <div className="ring-slots-section">
                            <div className="ring-slots-label desktop-only">Ring Slots</div>
                            <div className="ring-slots">
                              {[1, 2, 3].map((ringIndex) => {
                                // For ring slots, we need special handling
                                // Only slot 1 can have the equipment ring
                                // Show the equipped ring for each slot
                                const ringSlot = `ring${ringIndex}`;
                                const ringEquipped = equipData.equipped[ringSlot];
                                const isRingSlotOccupied = ringEquipped !== null;
                                const equippedName = isRingSlotOccupied ? ringEquipped : null;
                                const itemDetail = equippedName ? storeItemsByName[equippedName] : null;
                                const invRecord = itemDetail ? inventoryMap[itemDetail.id] : null;
                                
                                // Add a special class for ring slots 2 and 3 to indicate they're available
                                const ringSlotClass = ringIndex > 1 ? 'ring-slot-available' : '';

                                return (
                                  <div 
                                    key={ringSlot} 
                                    className={`equipment-slot slot-ring ${isRingSlotOccupied ? 'occupied' : ''} ${ringSlotClass}`}
                                    title={equippedName || `Empty ring slot ${ringIndex}`}
                                  >
                                    {isRingSlotOccupied && itemDetail ? (
                                      <>
                                        <img
                                          src={`/${itemDetail.image}`}
                                          alt={equippedName}
                                          className="equipment-item-image"
                                        />
                                        <div className="equipment-actions">
                                          <button
                                            className="action-button unequip-button"
                                            onClick={() => handleUnequip(itemDetail.id)}
                                            title="Unequip item"
                                          >
                                            ✕
                                          </button>
                                        </div>
                                        <div className="equipment-tooltip">
                                          {equippedName}
                                          {invRecord && ` (${invRecord.stack_amount})`}
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        <div className="equipment-tooltip">
                                          Empty ring slot {ringIndex}
                                        </div>
                                        <div className="ring-number mobile-only">{ringIndex}</div>
                                      </>
                                    )}
                                    <div className="equipment-slot-label desktop-only">Ring {ringIndex}</div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>

                        <div className="equipment-manager-right">
                          {/* Inventory Section */}
                          <div className="inventory-section">
                            <div className="inventory-header">   
                              <div className="search-container">
                                <input
                                  type="text"
                                  className="search-input"
                                  placeholder="Search equipment..."
                                  value={searchQuery}
                                  onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                <div className="search-icon">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="11" cy="11" r="8"></circle>
                                    <path d="21 21l-4.35-4.35"></path>
                                  </svg>
                                </div>
                              </div>
                            </div>

                            <div className="inventory-grid">
                              {equipableInStock
                                .filter(eqItem => {
                                  const itemDetail = storeItemsMap[eqItem.id];
                                  return itemDetail && itemDetail.name.toLowerCase().includes(searchQuery.toLowerCase());
                                })
                                .map((eqItem) => {
                                  const itemDetail = storeItemsMap[eqItem.id];
                                  if (!itemDetail) return null;
                                  
                                  // Use slot from backend if available, otherwise determine from item properties
                                  let itemSlot = eqItem.slot;
                                  if (!itemSlot) {
                                    ["head", "handhelds", "feet", "neck", "body"].forEach((s) => {
                                      if (itemDetail[s] > 0) itemSlot = s;
                                    });
                                  }

                                  // Special handling for ring items
                                  let slotAvailable = true;
                                  if (itemSlot === 'ring') {
                                    // Check if this ring is already equipped in any slot
                                    const isAlreadyEquipped = 
                                      (equipData.equipped['ring1'] === itemDetail.name) ||
                                      (equipData.equipped['ring2'] === itemDetail.name) ||
                                      (equipData.equipped['ring3'] === itemDetail.name);
                                    
                                    // Count how many ring slots are occupied
                                    const isRing1Occupied = !!equipData.equipped['ring1'];
                                    const isRing2Occupied = !!equipData.equipped['ring2'];
                                    const isRing3Occupied = !!equipData.equipped['ring3'];
                                    
                                    // Available if already equipped or if there's at least one free slot
                                    slotAvailable = isAlreadyEquipped || !(isRing1Occupied && isRing2Occupied && isRing3Occupied);
                                  } else {
                                    // For other equipment types, check normally
                                    slotAvailable = !equipData.equipped[itemSlot] || 
                                                  equipData.equipped[itemSlot] === itemDetail.name;
                                  }
                                  
                                  // Check if we've reached the max stack amount
                                  const maxStackReached = eqItem.max_stack_amount > 0 && 
                                                        eqItem.stack_amount >= eqItem.max_stack_amount;
                                  
                                  // Check if item has a level requirement and if player meets it
                                  const hasLevelRequirement = LEVEL_REQUIREMENTS[eqItem.id] !== undefined;
                                  const meetsLevelRequirement = !hasLevelRequirement || miningLevel >= LEVEL_REQUIREMENTS[eqItem.id];
                                  
                                  // Check if all ring slots are occupied (for ring items)
                                  const allRingSlotsOccupied = itemSlot === 'ring' && 
                                    !!equipData.equipped['ring1'] && 
                                    !!equipData.equipped['ring2'] && 
                                    !!equipData.equipped['ring3'] &&
                                    equipData.equipped['ring1'] !== itemDetail.name &&
                                    equipData.equipped['ring2'] !== itemDetail.name &&
                                    equipData.equipped['ring3'] !== itemDetail.name;
                                  
                                  // Can equip only if slot is available, max stack not reached, and meets level requirement
                                  const canEquip = slotAvailable && !maxStackReached && meetsLevelRequirement;

                                  return (
                                    <div 
                                      key={eqItem.id} 
                                      className={`inventory-item equipable ${canEquip ? 'can-equip' : ''}`}
                                    >
                                      <img
                                        src={`/${itemDetail.image}`}
                                        alt={itemDetail.name}
                                        className="inventory-item-image"
                                      />
                                      <div className="inventory-item-name">{itemDetail.name}</div>
                                      <div className="inventory-item-quantity">{eqItem.amount}</div>
                                      <div className="inventory-item-slot">{itemSlot}</div>
                                      <button
                                        className="equip-button"
                                        onClick={() => handleEquip(eqItem.id)}
                                        disabled={!canEquip}
                                        title={hasLevelRequirement && !meetsLevelRequirement ? 
                                            `Requires Mining Level ${LEVEL_REQUIREMENTS[eqItem.id]}` : 
                                            allRingSlotsOccupied ? 'All ring slots are occupied' :
                                            !slotAvailable ? 'This slot is already occupied' : 
                                            maxStackReached ? 'Maximum number already equipped' : 'Equip this item'}
                                      >
                                        {allRingSlotsOccupied ? 'All Slots Full' :
                                         !slotAvailable && itemSlot !== 'ring' ? 'Slot Occupied' : 
                                         maxStackReached ? 'Max Equipped' : 
                                         (hasLevelRequirement && !meetsLevelRequirement) ? 
                                         `Requires Level ${LEVEL_REQUIREMENTS[eqItem.id]}` : 'Equip'}
                                      </button>
                                    </div>
                                  );
                                })}
                            </div>
                            
                            {equipableInStock.length === 0 && (
                              <div style={{textAlign: 'center', color: '#b8c5d6', padding: '40px'}}>
                                <p>No equipable items available</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </TabPane>
                {/* Chests Tab */}
                <TabPane tabId="chests">
                  {loading ? (
                    <div className="loading-container">
                      <div className="loading-spinner"></div>
                      <span>Loading chests...</span>
                    </div>
                  ) : (
                    <div className="equipment-container chests-shell">
                      <div className="equipment-header chests-header">
                        <h1 className="equipment-title">Color Chests</h1>
                        <p className="equipment-subtitle">Open chests to discover valuable rewards</p>
                      </div>

                      <div className="inventory-section chests-panel">
                        <div className="inventory-header chests-toolbar">    
                          <div className="search-container chests-search">
                            <input
                              type="text"
                              className="search-input"
                              placeholder="Search chests..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <div className="search-icon">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="21 21l-4.35-4.35"></path>
                              </svg>
                            </div>
                          </div>
                        </div>

                        <div className="inventory-grid chests-grid">
                          {chestInventory
                            .filter(invItem => {
                              const item = storeItemsMap[invItem.item];
                              return item && item.name.toLowerCase().includes(searchQuery.toLowerCase());
                            })
                            .map((invItem) => {
                              const item = storeItemsMap[invItem.item];
                              if (!item) return null;
                              const chestTone = getChestTone(item.name);

                              return (
                                <div
                                  key={invItem.item}
                                  className={`inventory-item chest-item chest-card chest-card--${chestTone}`}
                                >
                                  <img
                                    src={`/${item.image}`}
                                    alt={item.name}
                                    className="inventory-item-image"
                                  />
                                  <div className="inventory-item-name chest-name">{item.name}</div>
                                  <div className="inventory-item-quantity chest-qty">
                                    {formatNumber(invItem.amount)}
                                  </div>
                                  <button
                                    className="open-chest-button chest-open-button"
                                    onClick={() =>
                                      safeItemIds.includes(invItem.item)
                                        ? handleUseItem(invItem.item)
                                        : handleUseChest(invItem.item)
                                    }
                                  >
                                    Open Chest
                                  </button>
                                </div>
                              );
                            })}
                        </div>
                        
                        {chestInventory.length === 0 && (
                          <div style={{textAlign: 'center', color: '#b8c5d6', padding: '40px'}}>
                            <p>No chests available to open</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </TabPane>

                <TabPane tabId="candies">
                  {candiesLoading || !candiesState ? (
                    <div className="loading-container">
                      <div className="loading-spinner"></div>
                      <span>Loading...</span>
                    </div>
                  ) : (
                    <Card className="candies-shell">
                      <CardBody className="candies-body">
                        <Row className="align-items-center candies-header">
                          <Col md="2" className="text-center candies-avatar-col">
                            <img
                              src={`/avatars/${candiesState?.avatar || localStorage.getItem('avatar') || '1'}.png`}
                              alt="Avatar"
                              className="candies-avatar"
                              onError={(e) => {
                                e.target.src = '/avatars/default.png';
                              }}
                            />
                          </Col>
                          <Col md="10">
                            <div className="candies-title">
                              <h4>Candies</h4>
                              <div className="candies-subtitle">
                                Use candies to gain XP in different categories.
                              </div>
                            </div>
                          </Col>
                        </Row>

                        <div className="candies-grid">
                          {CANDY_UI.map((cfg) => {
                            const prog = candiesState?.progress?.[cfg.key] || {};
                            const lvl = Number(prog.level || 1);
                            const exp = Number(prog.exp || 0);
                            const curMin = Number(prog.current_level_exp || 0);
                            const next = Number(prog.next_level_exp || 0);

                            const candyRow = (candiesState?.candies || []).find((c) => Number(c.id) === Number(cfg.candyId));
                            const amount = Number(candyRow?.amount || 0);
                            if (amount <= 0) return null;

                            const isHot = lastCandyCategory === cfg.key;

                            return (
                              <div
                                key={cfg.key}
                                className={`candies-card ${isHot ? "is-hot" : ""}`}
                                style={{
                                  background: cfg.bg,
                                  border: cfg.border,
                                  boxShadow: isHot ? cfg.shadow : undefined,
                                }}
                              >
                                <img src={cfg.icon} alt={cfg.label} className="candies-icon" />

                                <div className="candies-info">
                                  <div className="candies-info-row">
                                    <span className="candies-label">
                                      {cfg.label}
                                    </span>
                                    <span className="candies-count">
                                      ×{amount.toLocaleString()}
                                    </span>
                                  </div>
                                  <span className="candies-level" style={{ color: cfg.accent }}>
                                    Lv. {lvl.toLocaleString()}
                                  </span>
                                  <div className="candies-xp">
                                    XP {Math.max(0, exp - curMin).toLocaleString()} / {Math.max(1, next - curMin).toLocaleString()}
                                  </div>
                                  <div className="candies-progress">
                                    <div
                                      className="candies-progress-fill"
                                      style={{ width: `${Math.min(100, ((exp - curMin) / Math.max(1, next - curMin)) * 100)}%` }}
                                    />
                                  </div>
                                </div>

                                <button
                                  onClick={() => handleUseCandy(cfg.candyId)}
                                  disabled={!!usingCandyId}
                                  className="candies-use-button"
                                >
                                  Use
                                </button>
                              </div>
                            );
                          })}
                        </div>

                        {ownedCandyRows.length === 0 && (
                          <div style={{ color: "rgba(255,255,255,0.75)", padding: "12px 0" }}>
                            You don't have any candies.
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  )}
                </TabPane>
              </TabContent>
            </Col>
          </Row>
        </Container>
      </div>
      {/* Regular popup for messages */}
      {showPopup && !chestOpening && (
        <div
          className="popup-message"
          style={{
            position: "fixed",
            top: "50px",
            left: "50%",
            transform: "translateX(-50%)",
            padding: "15px",
            backgroundColor: popupColor,
            color: popupColor === "gray" ? "black" : "white",
            zIndex: 9999,
            border: "2px solid white",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            textAlign: "center",
          }}
        >
          {popupMessage}
        </div>
      )}
      
      {/* Chest Opening Modal */}
      {chestOpening && (
        <div className="chest-modal-overlay">
          <div className="chest-modal">
            <div className="chest-modal-header">
              <h5>{chestModalTitle}</h5>
              <button
                onClick={() => setChestOpening(false)}
                className="chest-modal-close"
              >
                ✕
              </button>
            </div>
            <div className="chest-modal-body">
              {chestDisplayImage && (
                <div className="chest-modal-image">
                  <img
                    src={chestDisplayImage}
                    alt="Chest Opening"
                  />
                </div>
              )}
              <div className="chest-modal-message">
                {popupMessage || 'Opening chest...'}
              </div>

              {chestRewardItems && chestRewardItems.length > 0 && (
                <div className="chest-modal-rewards">
                  {chestRewardItems.map((reward) => {
                    const item = storeItemsMap[reward.item_id];
                    const img = item?.image ? `/${item.image}` : '/items/pokeball.png';
                    const name = item?.name || reward.name || `Item ${reward.item_id}`;
                    return (
                      <div key={reward.item_id} className="chest-modal-reward">
                        <img
                          src={img}
                          alt={name}
                          className="chest-modal-reward-icon"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/items/pokeball.png';
                          }}
                        />
                        <div className="chest-modal-reward-qty">x{reward.amount}</div>
                        <div className="chest-modal-reward-name">{name}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="chest-modal-footer">
              <button
                onClick={() => setChestOpening(false)}
                className="chest-modal-dismiss"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {loadoutToast && (
        <div className={`loadout-toast ${loadoutToast.tone || ""}`.trim()}>
          <div className="loadout-toast-title">{loadoutToast.title}</div>
          {Array.isArray(loadoutToast.items) && loadoutToast.items.length > 0 && (
            <div className="loadout-toast-icons">
              {loadoutToast.items.slice(0, 8).map((it) => {
                const item = storeItemsMap[it.item_id];
                const img = item?.image ? `/${item.image}` : null;
                const name = item?.name || `Item ${it.item_id}`;
                const amount = Number(it.amount || 0);
                return (
                  <div key={`loadout-toast-${it.item_id}`} className="loadout-toast-icon">
                    {img ? (
                      <img
                        src={img}
                        alt={name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="loadout-toast-icon-fallback" />
                    )}
                    {amount > 1 && <div className="loadout-toast-count">×{amount}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      
    </React.Fragment>
  );
};

export default ManageItems;
